<template>
  <div
    class="group flex px-3 py-1 relative"
    :class="[isOwn ? 'justify-end' : 'justify-start', (message.sendState === 'sending') ? 'opacity-70' : '', message.deletedAt ? 'opacity-60' : '']"
  >
    <div
      class="flex gap-2 max-w-[88%] min-w-0"
      :class="isOwn ? 'flex-row-reverse' : ''"
      @touchstart.passive="onTouchStart"
    >
      <!-- Avatar (only for other users — WhatsApp convention).
           Use explicit trim check so empty strings fall through to the
           default, and onerror to recover from broken URLs (expired Spotify
           CDN, deleted S3 keys, etc.) instead of showing a broken-image icon. -->
      <img
        v-if="!isOwn"
        :src="avatarSrc"
        :alt="message.author.name"
        class="w-7 h-7 rounded-full object-cover shrink-0 mt-auto"
        :class="message.author?.openForCollabs ? 'ring-2 ring-cyan-400/60' : ''"
        :title="message.author?.openForCollabs ? $t('chat.badges.collab_tooltip') : ''"
        loading="lazy"
        @error="onAvatarError"
      />

      <div class="min-w-0 max-w-full flex flex-col" :class="isOwn ? 'items-end' : 'items-start'">
        <!-- Header row (name + time) — shown only on other users' messages -->
        <div
          v-if="!isOwn"
          class="flex items-baseline gap-2 px-1 mb-0.5"
        >
          <span class="text-xs font-semibold text-white truncate">{{ message.author.name || 'User' }}</span>
          <span
            v-if="message.author?.openForCollabs"
            class="shrink-0 text-[8px] font-bold uppercase tracking-widest px-1 py-px rounded bg-cyan-400/15 text-cyan-300 border border-cyan-400/25"
            :title="$t('chat.badges.collab_tooltip')"
          >
            {{ $t('chat.badges.collab') }}
          </span>
          <span class="text-[10px] text-text-dim shrink-0">{{ timeLabel }}</span>
        </div>

        <!-- Bubble -->
        <div
          class="rounded-lg px-3 py-1.5 relative min-w-0 max-w-full"
          :class="bubbleClasses"
        >
          <!-- Quote-reply preview — collapsible header above the body, click
               to jump to the original. Tombstoned reply targets degrade to
               "(message deleted)". -->
          <ChatQuoteHeader
            v-if="message.replyToPreview && !message.deletedAt"
            :preview="message.replyToPreview"
            :is-own="isOwn"
            @jump="$emit('jump-to', $event)"
          />

          <!-- Inline edit textarea — replaces the body when the user clicks
               Edit on their own message. Saves via PATCH; the WS frame
               syncs other tabs. Esc cancels, Enter saves (Shift+Enter for
               newline, matching the composer convention). -->
          <div v-if="editing && !message.deletedAt" class="flex flex-col gap-1">
            <textarea
              ref="editTextareaEl"
              v-model="editDraft"
              class="w-full bg-white/[0.06] border border-white/[0.10] rounded-md px-2 py-1.5 text-sm text-white outline-none focus:border-primary/50 resize-none"
              rows="2"
              :placeholder="message.body || ''"
              @keydown.escape.prevent="cancelEdit"
              @keydown.enter.exact="onEditEnter"
            />
            <div class="flex items-center justify-end gap-1.5">
              <button
                type="button"
                class="px-2 py-0.5 text-[10px] font-semibold rounded text-text-muted hover:text-white hover:bg-white/5"
                @click="cancelEdit"
              >
                {{ $t('chat.edit.cancel') }}
              </button>
              <button
                type="button"
                class="px-2 py-0.5 text-[10px] font-bold rounded bg-primary text-black hover:bg-primary/90 disabled:opacity-40"
                :disabled="saving || !editDraft.trim()"
                @click="saveEdit"
              >
                {{ saving ? $t('chat.composer.sending') : $t('chat.edit.save') }}
              </button>
            </div>
          </div>

          <!-- Body: deleted tombstone, or text with clickable URLs -->
          <div v-else-if="message.deletedAt" class="text-sm">
            <em class="text-text-dim">{{ $t('chat.moderation.message_removed') }}</em>
          </div>
          <div v-else-if="message.body" class="text-sm whitespace-pre-wrap [overflow-wrap:anywhere]">
            <template v-for="(seg, i) in bodySegments" :key="i">
              <a
                v-if="seg.type === 'url'"
                :href="seg.value"
                target="_blank"
                rel="noopener noreferrer"
                :class="isOwn ? 'underline decoration-white/50 hover:decoration-white break-all' : 'text-primary underline decoration-primary/40 hover:decoration-primary break-all'"
              >{{ seg.value }}</a>
              <span
                v-else-if="seg.type === 'emoji' && brokenEmojis.has(seg.name)"
                :title="$t('chat.emojis.unavailable_tooltip', { name: seg.name })"
              >:{{ seg.name }}:</span>
              <img
                v-else-if="seg.type === 'emoji'"
                :src="seg.url"
                :alt="':' + seg.name + ':'"
                :title="':' + seg.name + ':'"
                class="inline-block align-text-bottom w-[22px] h-[22px] object-contain select-none"
                loading="lazy"
                draggable="false"
                @error="onEmojiError(seg.name)"
              />
              <!-- Native Unicode emoji rendered via Twemoji SVG so every
                   viewer sees the same glyph regardless of OS / browser /
                   installed emoji font. The fallback wrapper swaps to the
                   original char (system font) when the CDN image fails. -->
              <span
                v-else-if="seg.type === 'unicode_emoji' && brokenTwemoji.has(seg.codepoints)"
                class="inline-block align-text-bottom select-none"
                aria-hidden="false"
              >{{ seg.char }}</span>
              <img
                v-else-if="seg.type === 'unicode_emoji'"
                :src="seg.url"
                :alt="seg.char"
                class="inline-block align-text-bottom w-[18px] h-[18px] object-contain select-none"
                loading="lazy"
                draggable="false"
                @error="onTwemojiError(seg.codepoints)"
              />
              <span v-else>{{ seg.value }}</span>
            </template>
          </div>

          <!-- Translation footer line (inside the bubble so it stays attached
               even when the link-preview card appears below) -->
          <div
            v-if="translationFooter"
            class="mt-1 text-[10px] flex items-center gap-1.5 flex-wrap"
            :class="isOwn ? 'text-white/60 justify-end' : 'text-text-dim'"
          >
            <span v-if="translation?.status === 'failed'">{{ $t('chat.translate.failed') }}</span>
            <span v-else>
              {{ $t('chat.translate.from', { lang: sourceLangLabel }) }}
            </span>
            <button
              v-if="translation?.status === 'ok' && !translation.sameLang"
              type="button"
              class="underline"
              @click.stop="toggle"
            >
              {{ translation.showOriginal
                ? $t('chat.translate.show_translation')
                : $t('chat.translate.show_original') }}
            </button>
            <button
              v-else-if="translation?.status === 'failed'"
              type="button"
              class="underline"
              @click.stop="retry"
            >
              {{ $t('chat.translate.retry') }}
            </button>
          </div>

          <!-- Image attachments -->
          <div
            v-if="!message.deletedAt && imageAttachments.length > 0"
            class="mt-1.5 flex gap-1.5 flex-wrap"
            :class="isOwn ? 'justify-end' : ''"
          >
            <a
              v-for="(att, i) in imageAttachments"
              :key="att.url || i"
              :href="failedAttachments.has(att.url) ? null : att.url"
              :target="failedAttachments.has(att.url) ? null : '_blank'"
              rel="noopener noreferrer"
              class="block rounded-md overflow-hidden bg-black/30 border border-white/5 max-w-[240px]"
            >
              <div
                v-if="failedAttachments.has(att.url)"
                class="flex items-center gap-2 p-3 text-xs text-text-muted italic"
                :title="'Could not load image — access denied or removed'"
              >
                <NuxtIcon name="mdi:image-off-outline" class="text-base" />
                Image unavailable
              </div>
              <img
                v-else
                :src="att.url"
                :alt="'image from ' + (message.author.name || 'user')"
                class="max-h-48 w-auto object-contain"
                loading="lazy"
                @error="failedAttachments.add(att.url)"
              />
            </a>
          </div>

          <!-- Footer: own-message timestamp + send status (WhatsApp-style inside the bubble) -->
          <div
            v-if="isOwn"
            class="flex items-center gap-1 justify-end mt-0.5 text-[10px] text-white/60"
          >
            <span v-if="message.sendState === 'sending'">{{ $t('chat.composer.sending') }}</span>
            <template v-else-if="message.sendState === 'failed'">
              <span class="text-red-300">{{ $t('chat.errors.send_failed') }}</span>
              <button class="underline" @click="$emit('retry', message.clientMsgId)">{{ $t('chat.actions.retry') }}</button>
            </template>
            <template v-else>
              <span v-if="message.editedAt" class="italic opacity-80">{{ $t('chat.edit.edited_label') }} ·</span>
              <span>{{ timeLabel }}</span>
            </template>
          </div>
          <!-- Reverse: also surface "edited" on other users' messages. The
               timestamp lives in the row header for those, so put the
               marker inside the bubble footer to keep it close to the body. -->
          <div
            v-if="!isOwn && message.editedAt && !message.deletedAt && !editing"
            class="text-[10px] text-text-dim italic mt-0.5"
          >{{ $t('chat.edit.edited_label') }}</div>
        </div>

        <!-- Reactions row — pills with counts, click to toggle. Renders
             aligned with the bubble's edge. -->
        <ChatReactionPills
          v-if="!message.deletedAt && (message.reactions?.length || 0) > 0"
          :message-id="message.id"
          :reactions="message.reactions"
          :is-own="isOwn"
        />

        <!-- Music share cards — rich previews for /releases/:id, /tracks/:id,
             /artists/:id URLs detected in the body. Renders ABOVE generic
             link previews so the artist's release stays the visual anchor
             of the message. Each card lazily fetches its own metadata so
             the wire payload stays a plain URL. -->
        <div
          v-if="!message.deletedAt && shareTargets.length > 0"
          class="flex flex-col gap-1.5 w-full"
        >
          <ChatShareCard
            v-for="(s, i) in shareTargets"
            :key="`${s.type}-${s.id}-${i}`"
            :type="s.type"
            :id="s.id"
            :raw-url="s.rawUrl"
          />
        </div>

        <!-- Link previews (outside the bubble so they can render full-width) -->
        <div
          v-if="!message.deletedAt && hydratedLinks.length > 0"
          class="mt-1.5 flex flex-col gap-1.5 w-full"
        >
          <ChatLinkPreviewCard
            v-for="link in hydratedLinks"
            :key="link.url"
            :url="link.url"
            :preview="link.preview"
          />
        </div>
      </div>

      <!-- Hover action toolbar: React / Reply / Edit / kebab. The kebab
           still hosts the rarer actions (delete, mute, pin) so the
           visible row stays compact. It floats over the top-right corner
           of the ROW (the outer div is the positioned ancestor) instead of
           sitting next to the bubble: in the flow it reserved its width on
           every message even while invisible, which squeezed the bubbles.
           `focus-within` also reveals it for keyboard users; `touched`
           flips it on touch (set by the bubble's @touchstart). -->
      <div
        v-if="!message.deletedAt"
        class="absolute -top-5 right-2 z-20 flex items-center gap-0.5 p-0.5 rounded-md bg-[var(--surface-hover,#16181e)] border border-white/10 shadow-lg opacity-0 pointer-events-none group-hover:opacity-100 group-hover:pointer-events-auto focus-within:opacity-100 focus-within:pointer-events-auto transition-opacity"
        :class="(touched || reactionPickerOpen) ? 'opacity-100 pointer-events-auto' : ''"
      >
        <!-- React — opens the reaction picker -->
        <button
          type="button"
          class="w-6 h-6 inline-flex items-center justify-center leading-none rounded text-text-muted hover:text-white hover:bg-white/5"
          :title="$t('chat.actions.react')"
          :aria-label="$t('chat.actions.react')"
          @click.stop="reactionPickerOpen = !reactionPickerOpen"
        >
          <span class="inline-block w-[14px] h-[14px] leading-[14px] text-center text-[12px]">😊</span>
        </button>
        <ChatReactionPicker
          v-model="reactionPickerOpen"
          :message-id="message.id"
          placement="right"
        />

        <!-- Reply -->
        <button
          type="button"
          class="w-6 h-6 inline-flex items-center justify-center leading-none rounded text-text-muted hover:text-white hover:bg-white/5"
          :title="$t('chat.actions.reply')"
          :aria-label="$t('chat.actions.reply')"
          @click.stop="$emit('reply', message)"
        >
          <el-icon size="12"><ChatLineRound /></el-icon>
        </button>

        <!-- Edit — own messages only, non-deleted -->
        <button
          v-if="isOwn && !editing"
          type="button"
          class="w-6 h-6 inline-flex items-center justify-center leading-none rounded text-text-muted hover:text-white hover:bg-white/5"
          :title="$t('chat.actions.edit')"
          :aria-label="$t('chat.actions.edit')"
          @click.stop="beginEdit"
        >
          <el-icon size="12"><Edit /></el-icon>
        </button>

        <!-- Kebab — pin / delete / mute -->
        <el-dropdown
          v-if="showKebab"
          trigger="click"
          :placement="isOwn ? 'bottom-start' : 'bottom-end'"
        >
          <button
            type="button"
            class="w-6 h-6 inline-flex items-center justify-center leading-none rounded text-text-muted hover:text-white hover:bg-white/5"
            aria-label="Message actions"
            @click.stop
          >
            <el-icon size="12"><MoreFilled /></el-icon>
          </button>
          <template #dropdown>
            <el-dropdown-menu>
              <el-dropdown-item
                v-if="canPin"
                @click="$emit('pin', message.id)"
              >
                <el-icon size="12" class="mr-1"><Top /></el-icon>
                {{ $t('chat.actions.pin') }}
              </el-dropdown-item>
              <el-dropdown-item v-if="canDelete" @click="$emit('delete', message.id)">
                <el-icon size="12" class="mr-1"><Delete /></el-icon>
                {{ $t('chat.actions.delete') }}
              </el-dropdown-item>
              <el-dropdown-item v-if="canMute" divided @click="$emit('mute', { userId: message.author.id, userName: message.author.name })">
                <span class="text-red-400">Mute user…</span>
              </el-dropdown-item>
            </el-dropdown-menu>
          </template>
        </el-dropdown>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, ref, reactive, watch, onMounted, nextTick } from 'vue'
import { MoreFilled, Edit, Delete, Top, ChatLineRound } from '@element-plus/icons-vue'
import { useChatTranslation } from '~/composables/chat/useChatTranslation'
import { useCustomEmoji, EMOJI_TOKEN_RE } from '~/composables/chat/useCustomEmoji'
import { splitUnicodeEmoji } from '~/utils/twemoji'

// Mirror of server/utils/chatLanguages.ts so we don't reach across the
// server/client boundary for static display labels. Keep in sync with that
// file when adding languages.
const SUPPORTED_LANGS = [
  { code: 'en', label: 'English' },
  { code: 'de', label: 'Deutsch' },
  { code: 'es', label: 'Español' },
  { code: 'fr', label: 'Français' },
  { code: 'it', label: 'Italiano' },
  { code: 'pt', label: 'Português' },
  { code: 'nl', label: 'Nederlands' },
  { code: 'pl', label: 'Polski' },
  { code: 'tr', label: 'Türkçe' },
  { code: 'ru', label: 'Русский' },
  { code: 'ja', label: '日本語' },
  { code: 'zh-Hans', label: '中文' }
]

const props = defineProps({
  message: { type: Object, required: true },
  // Permission flags passed in by the parent (ThreadView / GlobalRoom)
  // so the action menu surfaces "Pin" only when the viewer can act on it.
  // Defaults keep existing call sites working without prop additions.
  canPinMessages: { type: Boolean, default: false }
})
const emit = defineEmits(['retry', 'delete', 'mute', 'reply', 'pin', 'jump-to'])

const { user } = useUserSession()
const {
  onMessageArrival,
  toggleShowOriginal,
  retryTranslation,
  getState
} = useChatTranslation()
const { resolveEmoji, ensureLoaded: ensureEmojiCatalogLoaded } = useCustomEmoji()

// Load the tenant emoji catalog once — subsequent items share the cache.
// Safe to call from every MessageItem; `ensureLoaded` is idempotent.
ensureEmojiCatalogLoaded()

// Track per-attachment image-load failures (404 / 403 from the media proxy).
// Reactive Set so the v-if branch flips when `add()` fires from @error.
const failedAttachments = reactive(new Set())

const myId = computed(() => {
  const u = user.value
  return u ? String(u.id || u._id || '') : ''
})

const isOwn = computed(() => {
  if (!myId.value) return false
  return String(props.message.author?.id || '') === myId.value
})

const DEFAULT_AVATAR = '/images/default-avatar.svg'

// Flip to true when the remote avatar URL errors — serves as a sticky
// marker for the lifetime of this message item so we don't retry broken URLs.
const avatarBroken = ref(false)

const avatarSrc = computed(() => {
  if (avatarBroken.value) return DEFAULT_AVATAR
  const raw = (props.message.author?.avatar || '').trim()
  return raw || DEFAULT_AVATAR
})

function onAvatarError() {
  avatarBroken.value = true
}

// Reactive Set of shortcodes whose <img alt=""> failed to load (404 from the
// CDN, public-read ACL revoked, etc.). The renderer reads this and
// falls back to the literal `:shortcode:` text so the user sees what
// the sender meant, not a vanished glyph or a broken-image icon.
const brokenEmojis = reactive(new Set())
function onEmojiError(name) {
  if (name) brokenEmojis.add(name.toLowerCase())
}

// Twemoji CDN failure (slow network, CDN block, missing codepoint
// sequence in our Twemoji version) → swap the <img alt=""> for the original
// Unicode char so the user still sees something readable rendered by
// their OS font. Keyed by codepoint string so two different broken
// emojis stay independent.
const brokenTwemoji = reactive(new Set())
function onTwemojiError(codepoints) {
  if (codepoints) brokenTwemoji.add(codepoints)
}

/* ───────────── Auto-translation ───────────── */

const preferredLanguage = computed(() => user.value?.chatProfile?.preferredLanguage || null)

// Reactive per-message translation state. `getState` returns a reactive
// Map entry, so reading it inside a computed tracks updates.
const translation = computed(() => getState(props.message.id))

const shouldShowTranslated = computed(() => {
  const s = translation.value
  if (!s) return false
  if (s.status !== 'ok') return false
  if (s.sameLang) return false
  if (s.showOriginal) return false
  return !!s.translatedText
})

const translationFooter = computed(() => {
  const s = translation.value
  if (!s) return false
  if (s.status === 'failed') return true
  if (s.status !== 'ok') return false
  if (s.sameLang) return false
  return !!s.translatedText
})

const sourceLangLabel = computed(() => {
  const code = translation.value?.sourceLang
  if (!code) return ''
  const match = SUPPORTED_LANGS.find(l => l.code === code)
  return match ? match.label : code.toUpperCase()
})

function toggle() {
  toggleShowOriginal(props.message.id)
}
function retry() {
  retryTranslation(props.message, preferredLanguage.value, myId.value || null)
}

// Trigger translation on mount + whenever the message id or preferred
// language changes. clearAndRehydrate() on a language switch wipes the
// singleton state, which flips `translation` back to undefined and makes
// this watcher fire again — the re-entry path.
//
// The watcher MUST NOT use an array-literal getter — Vue compares the
// returned value with `===`, so `[a, b, c] !== [a, b, c]` is always
// true and the callback would fire on every reactive mutation inside
// the deps. With `onMessageArrival` re-enqueueing on failed status,
// that combination produced an endless 429 retry loop. Compose a
// primitive key string instead so unchanged-deps don't re-trigger.
function tryTranslate() {
  onMessageArrival(props.message, preferredLanguage.value, myId.value || null)
}
onMounted(tryTranslate)
watch(
  () => `${props.message.id}|${preferredLanguage.value || ''}|${translation.value === undefined}`,
  tryTranslate
)

const bubbleClasses = computed(() => {
  if (props.message.deletedAt) {
    return 'bg-white/[0.04] border border-white/5'
  }
  return isOwn.value
    ? 'bg-primary/25 text-white border border-primary/20'
    : 'bg-white/[0.06] text-text-main border border-white/5'
})

const isAdmin = computed(() => user.value?.role === 'admin')

// Author may delete own messages; admin may delete any message (moderation).
const canDelete = computed(() => {
  if (props.message.deletedAt) return false
  if (props.message.sendState === 'sending' || props.message.sendState === 'failed') return false
  return isOwn.value || isAdmin.value
})

// Mute is an admin-only action on other users' messages.
const canMute = computed(() => {
  if (props.message.deletedAt) return false
  if (!isAdmin.value) return false
  if (isOwn.value) return false
  return true
})

// Pin gating: parent passes `canPinMessages` based on thread kind + role
// (admin for global, creator/admin for groups, both participants for DMs).
// The kebab is rendered when ANY of pin/delete/mute is allowed.
const canPin = computed(() => !!props.canPinMessages && !props.message.deletedAt)
const showKebab = computed(() => canDelete.value || canMute.value || canPin.value)

// ─── Inline edit state ───────────────────────────────────────────────
// Own messages only. Begin → swap body for textarea, save → PATCH +
// optimistic local update (WS frame from the server resyncs other tabs).
const editing = ref(false)
const editDraft = ref('')
const saving = ref(false)
const editTextareaEl = ref(null)

function beginEdit() {
  editDraft.value = props.message.body || ''
  editing.value = true
  nextTick(() => {
    const el = editTextareaEl.value
    if (el) {
      el.focus()
      const len = el.value.length
      el.setSelectionRange(len, len)
    }
  })
}
function cancelEdit() {
  editing.value = false
  editDraft.value = ''
}
// IME guard — when CJK / Korean composition is in progress, Enter just
// commits the IME candidate and must NOT trigger save. Same guard as
// the composer's onEnter; bare Enter without composition saves.
function onEditEnter(e) {
  if (e.isComposing || e.keyCode === 229) return
  e.preventDefault()
  saveEdit()
}
async function saveEdit() {
  const trimmed = editDraft.value.trim()
  if (!trimmed || saving.value) return
  if (trimmed === (props.message.body || '')) {
    editing.value = false
    return
  }
  saving.value = true
  try {
    const res = await $fetch(`/api/chat/messages/${props.message.id}`, {
      method: 'PATCH',
      body: { body: trimmed }
    })
    // Optimistic local patch — the WS frame the server publishes is
    // authoritative but lands a tick later.
    if (res?.message) {
      Object.assign(props.message, res.message)
    } else {
      props.message.body = trimmed
      props.message.editedAt = new Date().toISOString()
    }
    editing.value = false
  } catch (err) {
    console.error('[chat:edit] save failed', err)
  } finally {
    saving.value = false
  }
}

// Reaction picker visibility — toggled by the React button. Closed via
// click-outside inside the picker itself.
const reactionPickerOpen = ref(false)

// Touch-reveal — the hover toolbar relies on :hover, which never fires
// on touch devices. Tap on the bubble flips this ref true for ~3 s so
// mobile users can reach React/Reply/Edit/kebab. Auto-clears so the row
// doesn't permanently stick highlighted.
const touched = ref(false)
let touchTimer = null
function onTouchStart() {
  touched.value = true
  if (touchTimer) clearTimeout(touchTimer)
  touchTimer = setTimeout(() => { touched.value = false }, 3000)
}

const timeLabel = computed(() => {
  const d = new Date(props.message.createdAt)
  if (isNaN(d.getTime())) return ''
  const now = new Date()
  const sameDay = d.toDateString() === now.toDateString()
  return sameDay
    ? d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    : d.toLocaleDateString([], { month: 'short', day: 'numeric' }) + ' ' +
      d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
})

const imageAttachments = computed(() =>
  (props.message.attachments || []).filter((a) => a?.type === 'image' && a?.url)
)

const hydratedLinks = computed(() =>
  (props.message.links || []).filter((l) => l?.preview && (l.preview.title || l.preview.imageUrl || l.preview.description))
)

/**
 * Detect music-asset URLs in the message body (or its known links) and
 * surface them as rich ChatShareCard previews. Pattern matches both
 * absolute (https://host/releases/abc...) and relative (/releases/abc)
 * forms because the composer always inserts absolute URLs, but
 * historical messages may have either. Dedupes by `${type}:${id}` to
 * stop the same paste rendering twice when the body contains a URL the
 * server also picked up as a "link" via bootstrapMessageLinks.
 */
const SHARE_URL_RE = /(?:https?:\/\/[^/\s]+)?\/(releases|tracks|artists)\/([a-f0-9]{24})\b/gi
const shareTargets = computed(() => {
  const out = []
  const seen = new Set()
  const candidateStrings = []
  if (props.message.body) candidateStrings.push(String(props.message.body))
  for (const l of (props.message.links || [])) {
    if (l?.url) candidateStrings.push(String(l.url))
  }
  for (const src of candidateStrings) {
    SHARE_URL_RE.lastIndex = 0
    let m
    while ((m = SHARE_URL_RE.exec(src)) !== null) {
      const segment = m[1].toLowerCase()
      const type = segment === 'releases' ? 'release' : segment === 'tracks' ? 'track' : 'artist'
      const id = m[2].toLowerCase()
      const key = `${type}:${id}`
      if (seen.has(key)) continue
      seen.add(key)
      out.push({ type, id, rawUrl: m[0] })
    }
  }
  return out
})

/**
 * Split body into text / url segments so URLs render as clickable links in-line.
 * Must mirror the server-side extractUrls regex closely enough that the same
 * URLs become anchors. Trailing punctuation is peeled off.
 */
const URL_RE = /(https?:\/\/[^\s<>"'`]+)/gi
const TRAILING = /[.,;:!?)\]}"'>`]+$/

/**
 * Second-pass segmentation: take a plain-text chunk and split on `:name:`
 * tokens that resolve to known custom emoji. Unknown tokens are left as
 * text so a stale shortcode just reads like `:missing_emoji:` instead of
 * erroring.
 *
 * Runs inside the reactive `bodySegments` computed, so when the emoji
 * catalog populates asynchronously, message bodies re-render automatically.
 */
function splitOnEmoji(text) {
  const out = []
  let lastIdx = 0
  let m
  const re = new RegExp(EMOJI_TOKEN_RE.source, 'g')
  while ((m = re.exec(text)) !== null) {
    const name = m[1]
    const entry = resolveEmoji(name)
    if (!entry) continue  // Unknown shortcode — leave as text
    const start = m.index
    const end = start + m[0].length
    if (start > lastIdx) out.push({ type: 'text', value: text.slice(lastIdx, start) })
    out.push({ type: 'emoji', name, url: entry.url })
    lastIdx = end
  }
  if (out.length === 0) return [{ type: 'text', value: text }]
  if (lastIdx < text.length) out.push({ type: 'text', value: text.slice(lastIdx) })
  return out
}

/**
 * Re-segment a plain text string into url/emoji/text nodes. Used in
 * two cases:
 *   - The active translation is being shown (translation runs on the
 *     plain body, not the AST).
 *   - The server didn't supply `bodyAst` (legacy frames cached before
 *     the server was upgraded). New messages always carry an AST so
 *     this branch becomes a no-op for fresh content.
 */
function segmentClientSide(text) {
  const urlOut = []
  let lastIdx = 0
  let m
  const re = new RegExp(URL_RE.source, 'gi')
  while ((m = re.exec(text)) !== null) {
    const start = m.index
    let raw = m[0]
    const end = start + raw.length
    if (start > lastIdx) urlOut.push({ type: 'text', value: text.slice(lastIdx, start) })
    const trailingMatch = raw.match(TRAILING)
    const trailing = trailingMatch ? trailingMatch[0] : ''
    if (trailing) raw = raw.slice(0, raw.length - trailing.length)
    urlOut.push({ type: 'url', value: raw })
    if (trailing) urlOut.push({ type: 'text', value: trailing })
    lastIdx = end
  }
  if (lastIdx < text.length) urlOut.push({ type: 'text', value: text.slice(lastIdx) })

  const out = []
  for (const seg of urlOut) {
    if (seg.type !== 'text') { out.push(seg); continue }
    for (const sub of splitOnEmoji(seg.value)) out.push(sub)
  }
  return out
}

const bodySegments = computed(() => {
  let segs
  // Translation pipeline runs against the plain body, so when the
  // user is viewing translated text we re-segment client-side.
  if (shouldShowTranslated.value) {
    const t = translation.value?.translatedText || props.message.body || ''
    segs = segmentClientSide(t)
  } else {
    // Server-rendered AST is the canonical path. Every server-sent
    // message carries `bodyAst` since the chat-body-AST migration.
    const ast = props.message.bodyAst
    if (Array.isArray(ast) && ast.length > 0) segs = ast
    // Pre-migration frames OR optimistic client-side messages that
    // never round-tripped: fall back to the client parser.
    else segs = segmentClientSide(props.message.body || '')
  }
  // Final pass: expand `text` segments with native Unicode emoji into
  // explicit `unicode_emoji` segments so the template renders them as
  // Twemoji SVGs. Identical glyphs across Mac/Win/Linux/iOS/Android —
  // critical for a multi-tenant chat where artists, LMs and admins
  // coordinate across mixed devices and Linux clients otherwise see
  // tofu boxes when their distro lacks a color emoji font.
  return expandUnicodeEmoji(segs)
})

/**
 * Walk the segment list once and replace each `text` node with its
 * Twemoji-expanded subsegments. Non-text segments (url / emoji shortcode
 * / unicode_emoji) pass through untouched.
 */
function expandUnicodeEmoji(segs) {
  const out = []
  for (const seg of segs) {
    if (seg?.type === 'text' && typeof seg.value === 'string' && seg.value) {
      const expanded = splitUnicodeEmoji(seg.value)
      for (const s of expanded) out.push(s)
    } else {
      out.push(seg)
    }
  }
  return out
}
</script>
